/**
 * Split and merge plans for one paragraph of an `IndexedStream` (LG-2859).
 *
 * The caret index is LG-2856's editable insert index. `capitalize.index` is where that
 * code point sits after the new marker is inserted, so the applier can write both in one pass.
 */

import { ParagraphMergeDirection } from 'src/models/TranscriptOperation'

import { MARKER_KINDS } from './constants'
import { IndexedStream, StreamEmbed, indexedOpLength, rangesFromStream } from './incremental'
import { editableInsertIndex } from './opTranslation'

export type ParagraphStructurePlan = {
    markerIndex: number
    capitalize?: { index: number; from: string; to: string }
    selection: { paragraphId: string; offset: number }
}

const isParagraphChangeEmbed = (embed: StreamEmbed): boolean => embed.kind === MARKER_KINDS.paragraphChange

const utteranceFinalOf = (embed: StreamEmbed): boolean | null => {
    if (embed.kind !== MARKER_KINDS.utteranceChange || typeof embed.final !== 'boolean') {
        return null
    }

    return embed.final
}

type CodePointAt = { index: number; text: string; finalized: boolean }

/** First code point at or after `fromIndex`, before the next paragraph marker. */
const codePointAt = (stream: IndexedStream, fromIndex: number): CodePointAt | null => {
    let offset = 0
    let utteranceFinal: boolean | null = null

    for (const op of stream) {
        const length = indexedOpLength(op)
        if (offset + length <= fromIndex) {
            if (typeof op.insert !== 'string') {
                const final = utteranceFinalOf(op.insert)
                if (final !== null) {
                    utteranceFinal = final
                }
            }
            offset += length
            continue
        }

        if (typeof op.insert !== 'string') {
            if (isParagraphChangeEmbed(op.insert) && offset >= fromIndex) {
                return null
            }
            const final = utteranceFinalOf(op.insert)
            if (final !== null) {
                utteranceFinal = final
            }
            offset += length
            continue
        }

        const localStart = Math.max(0, fromIndex - offset)
        const point = op.insert.codePointAt(localStart)
        if (point === undefined) {
            offset += length
            continue
        }

        return {
            index: offset + localStart,
            text: String.fromCodePoint(point),
            finalized: utteranceFinal !== false,
        }
    }

    return null
}

const textLength = (stream: IndexedStream, rangeStart: number, rangeEnd: number, until: number = rangeEnd): number => {
    const end = Math.min(until, rangeEnd)
    let offset = 0
    let count = 0

    for (const op of stream) {
        const length = indexedOpLength(op)
        if (offset >= end) {
            break
        }
        if (typeof op.insert === 'string') {
            const from = Math.max(offset, rangeStart)
            const to = Math.min(offset + length, end)
            if (to > from) {
                count += to - from
            }
        }
        offset += length
    }

    return count
}

const capitalizeOf = (point: CodePointAt | null): ParagraphStructurePlan['capitalize'] => {
    if (point === null || !point.finalized) {
        return undefined
    }

    const upper = point.text.toUpperCase()
    if (upper === point.text) {
        return undefined
    }

    return { index: point.index + 1, from: point.text, to: upper }
}

/**
 * Plan an Enter at `editableOffset` in `paragraphId`.
 * Returns `null` when the paragraph is missing or the offset is not an editable insert.
 */
export const planParagraphSplit = (
    stream: IndexedStream,
    paragraphId: string,
    editableOffset: number,
    newParagraphId: string,
): ParagraphStructurePlan | null => {
    const markerIndex = editableInsertIndex(stream, paragraphId, editableOffset)
    if (markerIndex === null) {
        return null
    }

    const point = codePointAt(stream, markerIndex)
    const range = rangesFromStream(stream).find((item) => item.id === paragraphId)
    const selection =
        point !== null && !point.finalized
            ? {
                  paragraphId,
                  offset: range === undefined ? 0 : textLength(stream, range.start, range.start + range.length, markerIndex),
              }
            : { paragraphId: newParagraphId, offset: 0 }
    const capitalize = capitalizeOf(point)

    return capitalize === undefined ? { markerIndex, selection } : { markerIndex, capitalize, selection }
}

/**
 * Plan a Backspace at the start (`Previous`) or a Delete at the end (`Next`).
 * Returns `null` when `paragraphId` is missing or that side has no neighbor.
 */
export const planParagraphMerge = (
    stream: IndexedStream,
    paragraphId: string,
    direction: ParagraphMergeDirection,
): ParagraphStructurePlan | null => {
    const ranges = rangesFromStream(stream)
    const index = ranges.findIndex((item) => item.id === paragraphId)
    if (index < 0) {
        return null
    }

    if (direction === ParagraphMergeDirection.Previous) {
        if (index === 0) {
            return null
        }
        const previous = ranges[index - 1]
        const current = ranges[index]

        return {
            markerIndex: current.start,
            selection: {
                paragraphId: previous.id,
                offset: textLength(stream, previous.start, previous.start + previous.length),
            },
        }
    }

    if (index + 1 >= ranges.length) {
        return null
    }

    const current = ranges[index]

    return {
        markerIndex: current.start + current.length,
        selection: {
            paragraphId: current.id,
            offset: textLength(stream, current.start, current.start + current.length),
        },
    }
}
